/**
 * TREATX-LEGAL-PLATFORM-V1_6 — Legal Consent Gate (Presentation-only refresh)
 * ==========================================================================
 * PRESENTATION-ONLY changes. The Policy Evaluator / Recorder / Evidence
 * model / Policy Sets / Reconsent engine are completely UNCHANGED.
 *
 * What this component does (and does not):
 *
 *   ✔ It calls the SERVER-SIDE evaluator (POST /api/legal/evaluate) as
 *     the single authority for what must be (re-)accepted RIGHT NOW.
 *   ✔ It renders ONLY the delta — `missing_required` +
 *     `outdated_required` — plus `outstanding_optional` in a clearly
 *     separated section. Documents the user already accepted at the
 *     current version DO NOT appear.
 *   ✔ It supports pure-UI bundle grouping (e.g. Registration Bundle,
 *     Payment Bundle) so 2..N documents feel like one coherent
 *     review — evidence still records one row per document.
 *   ✔ Progress indicator, mobile-friendly stacking, per-doc expand,
 *     optional-vs-mandatory affordance, dark-mode neutral styling.
 *   ✔ Localisation hooks via react-i18next where available.
 *
 *   ✘ It NEVER decides which documents are required.
 *   ✘ It NEVER merges consent rows.
 *   ✘ It NEVER hides an outdated / missing / country-scoped-newly-
 *     required document — those come from the evaluator and are
 *     mandatory in the rendered set.
 *
 * The parent receives one `onAccept({ consents, noneRequired })` call
 * once every mandatory document is ticked. `consents[]` remains the
 * canonical ConsentReceipt[] payload (one entry per accepted
 * document, per-document evidence — as required by the sprint).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Shield, ExternalLink, Loader2, AlertCircle, Lock, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { SafeHtml } from './security/SafeHtml';
import api, { apiErrorMessage } from '../utils/api';

// Optional i18n — component works even when i18next isn't mounted.
let _i18n = null;
try { _i18n = require('react-i18next').useTranslation; } catch (_e) { _i18n = null; }
const _useT = () => {
  if (_i18n) {
    try { return _i18n().t; } catch (_e) { /* noop */ }
  }
  return (_k, fallback) => fallback;
};

const _lang = () => {
  try { return (navigator?.language || 'en').split('-')[0].toLowerCase(); }
  catch (_e) { return 'en'; }
};

// P0-PAY-STUCK-01 — both calls go through the shared axios instance so the
// FIND-H-08 cookie transport AND the double-submit `X-CSRF-Token` header are
// attached. A raw `fetch` omits the CSRF header, and `POST /api/legal/evaluate`
// is a state-changing method, so the gate was rejected with 403 CSRF_DENIED
// and could never resolve the agreements.
async function fetchPreflight(journeyKey, countryContext) {
  try {
    const res = await api.post('/legal/evaluate', {
      journey_key: journeyKey,
      country: countryContext || undefined,
      platform: 'web',
    });
    const data = res.data;
    if (!data?.success) throw new Error(data?.message || 'Legal preflight failed');
    return data.decision;
  } catch (e) {
    if (e?.response) throw new Error(apiErrorMessage(e, 'Legal preflight failed'));
    throw e;
  }
}

async function fetchContent(key, language, country) {
  const res = await api.get('/public/content/resolve', {
    params: { key, language: language || 'en', ...(country ? { country } : {}) },
  });
  return res.data;
}

/**
 * Default bundle definitions. UI-only. The evaluator still returns
 * individual documents; unmatched keys fall through to an "Additional"
 * bundle so nothing is ever hidden.
 */
const DEFAULT_BUNDLES = {
  subscription_purchase: [
    { id: 'subscription', label_key: 'legal.bundles.subscription', label_fallback: 'Subscription agreement', keys: ['provider_subscription_consent_text'] },
  ],
  payment_terms_acceptance: [
    { id: 'payment', label_key: 'legal.bundles.payment', label_fallback: 'Payment & cancellation', keys: ['terms_of_service', 'payment_terms_acceptance_text', 'cancellation_and_refund_conditions', 'refund_policy_v1'] },
  ],
  provider_registration: [
    { id: 'provider_agreement', label_key: 'legal.bundles.provider', label_fallback: 'Provider agreement', keys: ['kvkk_provider_consent_text'] },
  ],
  patient_registration: [
    { id: 'registration', label_key: 'legal.bundles.registration', label_fallback: 'Account & privacy', keys: ['terms_of_service', 'privacy_policy'] },
  ],
  medical_publication: [
    { id: 'medical', label_key: 'legal.bundles.medical', label_fallback: 'Medical data & sharing', keys: ['medical_data_processing_consent_text', 'medical_file_sharing_consent_text'] },
    { id: 'publication', label_key: 'legal.bundles.publication', label_fallback: 'Before/After publication', keys: ['before_after_publication_consent_text', 'marketing_usage_consent_text'] },
  ],
};

const _resolveBundles = (journeyKey, overrides) => {
  if (Array.isArray(overrides) && overrides.length) return overrides;
  return DEFAULT_BUNDLES[journeyKey] || [];
};

/**
 * Props:
 *   open            (bool)     : render only when true
 *   journeyKey      (string)   : evaluator journey (e.g. 'payment_terms_acceptance')
 *   countryContext  (string?)  : ISO-2 country override
 *   ctaLabel        (string?)  : primary CTA label
 *   subtitle        (string?)  : subheading text
 *   submitting      (bool)     : parent-owned submitting state
 *   onAccept        (fn)       : ({ consents, noneRequired }) => void
 *   onCancel        (fn)       : () => void
 *   testIdPrefix    (string?)  : data-testid namespace (default 'legal-gate')
 *   bundles         (array?)   : override bundle map for this journey
 *   showOptional    (bool)     : default true — surface outstanding_optional
 */
const LegalConsentGate = ({
  open,
  journeyKey,
  countryContext,
  ctaLabel,
  subtitle,
  submitting = false,
  failure = null,
  onAccept,
  onCancel,
  testIdPrefix = 'legal-gate',
  bundles: bundlesOverride,
  showOptional = true,
}) => {
  const t = _useT();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null);
  const [docs, setDocs] = useState({});               // {key: {title, body, ...}}
  const [accepted, setAccepted] = useState({});        // {key: bool} (mandatory)
  const [acceptedOpt, setAcceptedOpt] = useState({}); // optional accepts
  const [openBody, setOpenBody] = useState({});        // {key: bool}
  const [collapsedBundles, setCollapsedBundles] = useState({});

  // P0-PAY-STUCK-01 — `onAccept` is almost always an inline arrow from the
  // parent, so a parent re-render used to produce a NEW `load` identity, which
  // re-ran the `[open, load]` effect, which re-evaluated, which auto-continued,
  // which re-rendered the parent… an unbounded evaluate → checkout → re-render
  // loop (the observed flicker + permanent "Submitting…"). Holding the callback
  // in a ref keeps `load` stable, so the gate evaluates once per open.
  const onAcceptRef = useRef(onAccept);
  useEffect(() => { onAcceptRef.current = onAccept; });
  const autoContinuedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dec = await fetchPreflight(journeyKey, countryContext);
      // ── DELTA-ONLY set ──
      // Evaluator is the sole authority. Documents the user already
      // accepted at current_version do NOT appear here.
      const missing = (dec?.missing_required) || [];
      const outdated = (dec?.outdated_required) || [];
      const optional = showOptional ? ((dec?.outstanding_optional) || []) : [];

      // De-dupe by content_key; outdated takes precedence over missing
      // (informational only — both are equally mandatory today).
      const mandatoryMap = new Map();
      missing.forEach((r) => mandatoryMap.set(r.content_key, { ...r, _reason: 'missing' }));
      outdated.forEach((r) => mandatoryMap.set(r.content_key, { ...r, _reason: 'outdated' }));
      const mandatory = Array.from(mandatoryMap.values());
      const optMap = new Map();
      optional.forEach((r) => optMap.set(r.content_key, { ...r, _reason: 'optional' }));
      const optionalOnly = Array.from(optMap.values())
        .filter((o) => !mandatoryMap.has(o.content_key));

      if (!mandatory.length && !optionalOnly.length) {
        // User is fully compliant. Auto-continue — this is exactly the
        // "no re-approval of already accepted documents" invariant.
        setDecision(dec);
        setLoading(false);
        // Fires at most once per open — never re-dispatches the downstream
        // action (checkout) when the parent re-renders.
        if (autoContinuedRef.current) return;
        autoContinuedRef.current = true;
        onAcceptRef.current?.({ consents: [], noneRequired: true });
        return;
      }

      const lang = _lang();
      const allKeys = [...mandatory, ...optionalOnly].map((r) => r.content_key);
      const results = await Promise.all(allKeys.map(async (k) => {
        try {
          const d = await fetchContent(k, lang, countryContext);
          return [k, {
            content_key: k,
            title: d.title || k,
            body: d.body || '',
            summary: d.summary || '',
            version: d.version,
            language: d.resolved_language || lang,
            body_sha256: d.body_sha256 || null,
          }];
        } catch (e) {
          return [k, { content_key: k, title: k, body: '', summary: '', version: null, language: lang, body_sha256: null, _error: String(e?.message || e) }];
        }
      }));
      const docMap = Object.fromEntries(results);

      setDecision({ ...dec, mandatory, optionalOnly });
      setDocs(docMap);
      setAccepted(Object.fromEntries(mandatory.map((r) => [r.content_key, false])));
      setAcceptedOpt(Object.fromEntries(optionalOnly.map((r) => [r.content_key, false])));
      setOpenBody({});
      setCollapsedBundles({});
    } catch (e) {
      setError(e?.message || 'Could not load the legal step.');
    } finally {
      setLoading(false);
    }
  }, [journeyKey, countryContext, showOptional]);

  useEffect(() => {
    if (!open) {
      autoContinuedRef.current = false;
      return;
    }
    load();
  }, [open, load]);

  // Bundle layout for the current journey (UI-only). Any key not in any
  // bundle falls into an "Additional agreements" catch-all bundle so
  // nothing is ever hidden by mis-configured bundles.
  const layout = useMemo(() => {
    if (!decision) return { mandatory: [], optional: [] };
    const conf = _resolveBundles(journeyKey, bundlesOverride);
    const _placeIntoBundles = (docsList) => {
      if (!docsList.length) return [];
      const placed = new Set();
      const groups = conf.map((b) => ({
        ...b,
        items: docsList.filter((d) => (b.keys || []).includes(d.content_key)),
      })).filter((g) => g.items.length);
      groups.forEach((g) => g.items.forEach((it) => placed.add(it.content_key)));
      const leftover = docsList.filter((d) => !placed.has(d.content_key));
      if (leftover.length) {
        groups.push({
          id: 'additional',
          label_key: 'legal.bundles.additional',
          label_fallback: 'Additional agreements',
          items: leftover,
        });
      }
      return groups;
    };
    return {
      mandatory: _placeIntoBundles(decision.mandatory || []),
      optional: _placeIntoBundles(decision.optionalOnly || []),
    };
  }, [decision, journeyKey, bundlesOverride]);

  if (!open) return null;

  // ── Progress + Accept gating ──
  const mandatoryList = decision?.mandatory || [];
  const totalRequired = mandatoryList.length;
  const totalAccepted = mandatoryList.reduce((n, r) => n + (accepted[r.content_key] ? 1 : 0), 0);
  const allAccepted = totalRequired > 0 && totalAccepted === totalRequired;
  // LEGAL-GATE-RETRY-01 — the evaluator POSITIVELY confirmed that nothing more
  // has to be accepted. The gate used to auto-continue once and then sit there
  // with an empty body and a permanently disabled button, so a downstream
  // checkout failure was a dead end. Confirmed compliance is itself a valid
  // reason to enable the action, and pressing it re-dispatches the SAME
  // already-captured (empty) consent payload — no loop, no fabricated
  // acceptance, no re-asking for consent that is already valid.
  const noneRequired = !!decision && !loading && !error
    && !(decision.mandatory || []).length && !(decision.optionalOnly || []).length;
  const canSubmit = (allAccepted || noneRequired) && !submitting;

  const submitPayload = () => {
    if (noneRequired) {
      onAcceptRef.current?.({ consents: [], noneRequired: true, retry: true });
      return;
    }
    if (!allAccepted) return;
    // One consent receipt per document — never merged.
    const mandatoryReceipts = mandatoryList.map((r) => {
      const d = docs[r.content_key] || {};
      return {
        content_key: r.content_key,
        content_version: d.version || r.current_version || 1,
        language: d.language || _lang(),
        country_context: countryContext || null,
        acceptance_mechanism: 'checkbox',
      };
    });
    const optionalReceipts = (decision?.optionalOnly || [])
      .filter((r) => acceptedOpt[r.content_key])
      .map((r) => {
        const d = docs[r.content_key] || {};
        return {
          content_key: r.content_key,
          content_version: d.version || r.current_version || 1,
          language: d.language || _lang(),
          country_context: countryContext || null,
          acceptance_mechanism: 'checkbox_optional',
        };
      });
    onAcceptRef.current?.({ consents: [...mandatoryReceipts, ...optionalReceipts] });
  };

  const toggleBody = (k) => setOpenBody((p) => ({ ...p, [k]: !p[k] }));
  const toggleBundle = (id) => setCollapsedBundles((p) => ({ ...p, [id]: !p[id] }));

  const _renderRow = (d, isMandatory) => {
    const key = d.content_key;
    const doc = docs[key] || {};
    const isChecked = isMandatory ? !!accepted[key] : !!acceptedOpt[key];
    const isOpen = !!openBody[key];
    const reason = d._reason;
    const badge = reason === 'outdated'
      ? { text: t('legal.badges.updated', 'Updated'), cls: 'bg-amber-100 text-amber-700' }
      : reason === 'missing' && isMandatory
        ? { text: t('legal.badges.required', 'Required'), cls: 'bg-red-100 text-red-700' }
        : { text: t('legal.badges.optional', 'Optional'), cls: 'bg-slate-100 text-slate-600' };

    return (
      <div key={key}
        className={`rounded-lg border transition-colors ${isChecked ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white hover:border-slate-300'}`}
      >
        <div className="p-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isMandatory) setAccepted((p) => ({ ...p, [key]: !p[key] }));
              else setAcceptedOpt((p) => ({ ...p, [key]: !p[key] }));
            }}
            className="mt-1 h-4 w-4 shrink-0"
            data-testid={`${testIdPrefix}-checkbox-${key}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start sm:items-center justify-between gap-2 flex-col sm:flex-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900 truncate">
                    {doc.title || key}
                  </span>
                  {doc.version && (
                    <span className="text-[10px] text-slate-400">v{doc.version}</span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.cls}`}
                    data-testid={`${testIdPrefix}-badge-${key}`}>{badge.text}</span>
                </div>
                {doc.summary && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{doc.summary}</p>}
              </div>
              <button
                type="button"
                onClick={() => toggleBody(key)}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0"
                data-testid={`${testIdPrefix}-view-${key}`}
              >
                {isOpen ? t('legal.hide', 'Hide') : t('legal.view', 'View')}
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
            {isOpen && (
              <SafeHtml
                html={doc.body || t('legal.notPublished', '(Document body is not published yet.)')}
                className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700 max-h-64 overflow-y-auto"
                testId={`${testIdPrefix}-body-${key}`}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const _renderBundleGroup = (bundle, isMandatory) => {
    const collapsed = !!collapsedBundles[bundle.id];
    const label = t(bundle.label_key, bundle.label_fallback);
    const items = bundle.items || [];
    const done = items.reduce((n, d) => n + ((isMandatory ? accepted : acceptedOpt)[d.content_key] ? 1 : 0), 0);
    return (
      <div key={bundle.id} className="space-y-2" data-testid={`${testIdPrefix}-bundle-${bundle.id}`}>
        <button
          type="button"
          onClick={() => toggleBundle(bundle.id)}
          className="w-full flex items-center justify-between text-left text-xs uppercase tracking-wide text-slate-500 hover:text-slate-900"
          data-testid={`${testIdPrefix}-bundle-toggle-${bundle.id}`}
        >
          <span className="flex items-center gap-1.5">
            {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            <span>{label}</span>
            {isMandatory && (
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded ${done === items.length ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {done}/{items.length}
              </span>
            )}
          </span>
        </button>
        {!collapsed && (
          <div className="space-y-2 pl-4 border-l-2 border-slate-100">
            {items.map((d) => _renderRow(d, isMandatory))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-blue-600 mt-1 shrink-0" />
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">
            {t('legal.title', 'Legal Agreements')}
          </h3>
          <p className="text-xs text-slate-500">
            {subtitle || t('legal.subtitle', 'Please review and accept the applicable agreements to continue.')}
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid={`${testIdPrefix}-loading`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('legal.loading', 'Checking your agreements…')}
        </div>
      )}

      {/* LEGAL-GATE-RETRY-01 — the three states are distinct: checking
          agreements, nothing further to accept, and preparing the payment. */}
      {noneRequired && !submitting && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2"
          data-testid={`${testIdPrefix}-none-required`}
        >
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {t('legal.none_required',
              'Your agreements are up to date — nothing further to accept.')}
          </span>
        </div>
      )}

      {submitting && (
        <div className="flex items-center gap-2 text-sm text-blue-700" data-testid={`${testIdPrefix}-preparing`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('legal.preparing_payment', 'Preparing your secure payment…')}
        </div>
      )}

      {/* An ACTIONABLE downstream failure, with the backend's own code and a
          safe support reference — never collapsed into a generic message. */}
      {failure && !submitting && (
        <div
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 space-y-1"
          data-testid={`${testIdPrefix}-failure`}
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{failure.message}</span>
          </div>
          {(failure.code || failure.reference) && (
            <div className="text-[11px] text-red-600/80 pl-6" data-testid={`${testIdPrefix}-failure-ref`}>
              {failure.code ? `${t('legal.failure_code', 'Reference')}: ${failure.code}` : null}
              {failure.code && failure.reference ? ' · ' : null}
              {failure.reference || null}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && decision && (decision.mandatory?.length > 0 || decision.optionalOnly?.length > 0) && (
        <>
          {/* Progress indicator for mandatory documents */}
          {totalRequired > 0 && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 flex items-center gap-3" data-testid={`${testIdPrefix}-progress`}>
              <div className="flex-1">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                  <span>{t('legal.progress', 'Progress')}</span>
                  <span className="font-semibold">{totalAccepted}/{totalRequired}</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-200"
                    style={{ width: `${(totalAccepted / totalRequired) * 100}%` }}
                    data-testid={`${testIdPrefix}-progress-bar`}
                  />
                </div>
              </div>
              {allAccepted && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
            </div>
          )}

          {/* Mandatory bundles */}
          {layout.mandatory.map((b) => _renderBundleGroup(b, true))}

          {/* Optional bundles */}
          {layout.optional.length > 0 && (
            <div className="pt-2">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                {t('legal.optional_heading', 'Optional agreements')}
              </div>
              <div className="space-y-3">
                {layout.optional.map((b) => _renderBundleGroup(b, false))}
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-xs text-slate-500">
        <Lock className="inline h-3 w-3 mr-1" />
        {t('legal.evidence_hint', 'Each acceptance is recorded independently (document, version, body hash, timestamp) and linked to your internal transaction — completely independent of the payment processor.')}
      </p>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
        {onCancel && (
          <Button
            variant="outline"
            className="w-full sm:flex-1"
            onClick={onCancel}
            disabled={submitting}
            data-testid={`${testIdPrefix}-cancel`}
          >
            {t('legal.back', 'Back')}
          </Button>
        )}
        <Button
          className="w-full sm:flex-1 bg-green-600 hover:bg-green-700"
          disabled={!canSubmit}
          onClick={submitPayload}
          data-testid={`${testIdPrefix}-accept`}
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('legal.preparing_payment', 'Preparing your secure payment…')}
            </>
          ) : (
            (noneRequired && failure)
              ? t('legal.try_again', 'Try again')
              : (ctaLabel || t('legal.accept_continue', 'Accept & Continue'))
          )}
        </Button>
      </div>
    </div>
  );
};

export default LegalConsentGate;
